/**
 * @module app.shared
 */

import { autoinject, TaskQueue } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IAccordion, CollapseEvent } from 'app/shared/attributes/ess-collapse-attributes';
import { fireEvent } from 'app/shared/lib/fire-events';
import { IMenuItem, NavigationService } from 'app/shared/services/navigation-service';
import { getKey, Key } from 'app/shared/lib/keys';
import { canReceiveFocus, focusFirst } from 'app/shared/lib/focus';
import { guid } from 'app/shared/lib/guid';

export enum NavMenuClass {
    MenuItem = 'ess-nav-menu-focus-item',
    CurrentMenuContainer = 'ess-nav-menu-current',
    SearchBar = 'ess-nav-search'
}

const NavMenuSelector = {
    MenuItem: `.${NavMenuClass.MenuItem}`,
    ActiveMenuItem: `.${NavMenuClass.CurrentMenuContainer} > .${NavMenuClass.MenuItem}`,
    MenuGroupBody: '.collapsible-body',
    SearchBar: `.${NavMenuClass.SearchBar} input`,
    AppBody: 'mainView',
};

@autoinject
export class EssAppMenu {

    @bindable.array
    public menuItems: IMenuItem[] = [];

    @bindable.stringNull
    public currentHref: string | null = null;

    @bindable
    protected rootMenuElement: Element | null = null;

    protected focusedMenuItem: HTMLElement | null = null;

    @bindable
    protected headerGuidId = `app-menu-header-${guid()}`;
    @bindable
    protected bodyGuidId = `app-menu-body-${guid()}`;

    // refs
    protected navMenuElement!: Element;
    protected accordion!: IAccordion;

    protected initialFocusin: boolean = true; // determines whether focus should jump to active menu item

    constructor(
        private readonly nav: NavigationService,
        private readonly element: Element,
        protected readonly queue: TaskQueue) { }

    protected attached(): void {
        this.currentHrefChanged(this.currentHref, null);
    }

    ////****************** Events ***************************************************////
    protected handleMouseDown(event: MouseEvent) {
        // prevents any autofocusing procedures when user is interacting with the menu via mouse
        event.preventDefault();

        // set menu item focus
        const itemClicked = event.target as HTMLElement;
        const menuItemClicked = itemClicked.querySelector<HTMLElement>(NavMenuSelector.MenuItem) || itemClicked.closest(NavMenuSelector.MenuItem);
        if(!!menuItemClicked) {
            this.initialFocusin = false;
            this.queue.queueTask(() => menuItemClicked.focus());
        }
    }

    protected handleMenuFocusin(event: FocusEvent) {
        // if tabbing into component verify that this focus is occurring on the outermost ess-app-menu element
        if(!this.rootMenuElement && this.initialFocusin) {
            this.initialFocusin = false;
            const activeMenuItem = this.navMenuElement.querySelector<HTMLElement>(NavMenuSelector.ActiveMenuItem);
            if(!!activeMenuItem)
                fireEvent(activeMenuItem, 'focus-active');
        }
    }

    protected handleMenuFocusout({ relatedTarget: next }: FocusEvent) {
        this.focusedMenuItem = null;
        // only reset initialFocusin if the next item being focused is outside of the nav menu
        if(!next || !this.element.contains(next as Node)) {
            this.initialFocusin = true;
        }
    }

    protected handleMenuItemFocus(event: FocusEvent) {
        this.focusedMenuItem = event.target as HTMLElement;
    }

    protected handleKeydown(event: KeyboardEvent): boolean {
        if(!this.focusedMenuItem) {
            return true;
        }

        const rootElement = this.rootMenuElement ?? this.navMenuElement;
        // use root nav element to select all menu items within expanded sections of the menu
        const fullMenuItems = Array.from(rootElement.querySelectorAll<HTMLElement>(NavMenuSelector.MenuItem));
        const accessibleMenuItems = fullMenuItems.filter(menuItem => canReceiveFocus(menuItem));
        let focusedMenuItemIndex = accessibleMenuItems.indexOf(this.focusedMenuItem!);
        // isolate parent item index (if present)
        const parentMenuItem = this.navMenuElement.closest(NavMenuSelector.MenuGroupBody)?.previousElementSibling as HTMLElement;
        const parentMenuItemIndex = accessibleMenuItems.indexOf(parentMenuItem!);

        switch(getKey(event)) {
            // handle "click" on menu item
            case Key.Space:
                this.queue.queueTask(() => this.focusedMenuItem?.click());
                return false;
            // navigate to tab stops (search bar, active menu item, chat icon/main content)
            case Key.Tab:
                this.handleTab(fullMenuItems, !event.shiftKey);
                return false;
            // basic directional menu navigation
            case Key.Up:
            case Key.Left:
                focusedMenuItemIndex = (accessibleMenuItems.length + focusedMenuItemIndex - 1) % accessibleMenuItems.length;
                break;
            case Key.Down:
            case Key.Right:
                focusedMenuItemIndex = (focusedMenuItemIndex + 1) % accessibleMenuItems.length;
                break;
            // set index to first element in current sub-menu
            case Key.Home:
                focusedMenuItemIndex = (parentMenuItemIndex + 1) % accessibleMenuItems.length;
                break;
            // set index to last element in current sub-menu
            case Key.End:
                const lastMenuItem = Array.from(this.navMenuElement.children).slice(-1).pop()?.querySelector<HTMLElement>(NavMenuSelector.MenuItem);
                focusedMenuItemIndex = accessibleMenuItems.indexOf(lastMenuItem!) % accessibleMenuItems.length;
                break;
            // set focus to parent element and collapse current sub-menu
            case Key.Esc:
                if(parentMenuItem) {
                    this.queue.queueTask(() => parentMenuItem.focus());
                    this.queue.queueTask(() => parentMenuItem.click());
                }
                return false;
            default:
                return true;
        }

        // update the focused tab
        const newFocusedMenuItem = accessibleMenuItems[focusedMenuItemIndex];
        if(newFocusedMenuItem != this.focusedMenuItem) {
            this.queue.queueTask(() => newFocusedMenuItem?.focus());
        }

        return false;
    }

    ////****************** Functions ************************************************////
    public getIcon(menuItem: IMenuItem): string {
        return '';
    }

    public isGroup(menuItem: IMenuItem): boolean {
        return !menuItem.href; // !!(menuItem.children && menuItem.children.length > 0);
    }

    public hasChildren(menuItem: IMenuItem): boolean {
        return !!(menuItem.children && menuItem.children.length > 0);
    }

    public isCurrent(currentUrl: string, menuItem: IMenuItem): boolean {
        // TODO: we really need to match on route code.  this approach might not work long-term
        if(this.isGroup(menuItem)) { return false; }

        function normalizeUrl(url: string): string {
            if(!url) return '';
            const withoutOrigin = url.replace(/^[a-z]+:\/\/[^/]+/i, '');
            return (withoutOrigin.replace(/\/+$/, '').toUpperCase() || '/');
        }
        const current = normalizeUrl(currentUrl);
        const target = normalizeUrl(this.prefixUrl(menuItem));

        if(current === target || current.startsWith(target + '/')) { return true; }
        return false;
    }

    public prefixUrl(menuItem: IMenuItem): string {
        return this.nav.resolveUrl(menuItem);
    }

    protected currentHrefChanged(newHref: string | null, oldHref: string | null): void {
        if(newHref !== oldHref && newHref) {
            let index: number = 0;
            for(const item of this.menuItems) {
                // new url matches, expand item
                if(this.isCurrent(newHref, item)) {
                    fireEvent(this.element, 'menu-nav', { bubbles: true, detail: { index } });
                    // scroll into view
                    if(this.navMenuElement) {
                        const itemElement = Array.from(this.navMenuElement.children)[index];
                        if(itemElement) {
                            // delaying this (queue task didn't seem to work)
                            window.setTimeout(() => {
                                itemElement.scrollIntoView({ behavior: 'smooth' });
                            }, 250);
                        }
                    }
                    return;
                }
                index++;
            }
        }
    }

    protected focusActive(event: Event) {
        const activeItem = event.target as HTMLElement;
        const index = this.menuItems.findIndex((menuItem) => activeItem.getAttribute('href') === this.prefixUrl(menuItem));
        fireEvent(this.element, 'menu-nav', { bubbles: true, detail: { index, focusActive: true } });
    }

    protected childNavigate(event: CollapseEvent, groupIndex: number): void {
        if(this.accordion) {
            this.accordion.open(groupIndex);
            const focusActive = event.detail.focusActive;
            if(!!focusActive && !this.rootMenuElement) {
                const activeElement = (event.target as HTMLElement)?.querySelector<HTMLElement>(NavMenuSelector.ActiveMenuItem);
                this.queue.queueTask(() => activeElement?.focus());
            }
        }
    }

    protected groupOpened(event: CollapseEvent): void {
        const accordion = event.target;
        if(accordion === this.navMenuElement) {
            const index = event.detail.index;
            this.menuItems[index].isExpanded = true;
        }
    }

    protected groupClosed(event: CollapseEvent) {
        const accordion = event.target;
        if(accordion === this.navMenuElement) {
            const index = event.detail.index;
            this.menuItems[index].isExpanded = false;
        }
    }

    protected handleTab(menuItems: HTMLElement[], tabForward: boolean = true) {
        // isolate active item index
        const activeMenuItem = (this.rootMenuElement ?? this.navMenuElement).querySelector<HTMLElement>(NavMenuSelector.ActiveMenuItem);
        const activeMenuItemIndex = menuItems.indexOf(activeMenuItem!);
        const globalFocusedIndex = menuItems.indexOf(this.focusedMenuItem!);

        // if there is no active menu item, or the active item is not the next tab stop
        if(!activeMenuItem || (tabForward && globalFocusedIndex >= activeMenuItemIndex) || (!tabForward && globalFocusedIndex <= activeMenuItemIndex)) {
            if(tabForward) {
                const appBody = document.getElementById(NavMenuSelector.AppBody);
                if(!!appBody) this.queue.queueTask(() => focusFirst(appBody));
            } else {
                this.queue.queueTask(() => document.querySelector<HTMLElement>(NavMenuSelector.SearchBar)?.focus());
            }
        } else {
            fireEvent(activeMenuItem, 'focus-active');
        }
    }

}
